/**
 * The "Capture Issues" flow, with no URL prompt when the project already
 * knows its site.
 *
 * Emily, 2026-09-10: the modal asked for a Page URL on a project whose URL
 * the product already held ("it should just open the teamreview on the
 * default project url"). start() now creates the team review at the URL
 * resolveCaptureIssuesUrl found and opens it, the Schedule page's no-confirm
 * mechanics (SchedulePage.tsx handleCaptureIssues, ~1.2-1.6s click-to-usable
 * measured 2026-08-22). The modal survives for exactly three cases:
 *
 *   1. no URL is known for the project (source 'none');
 *   2. the URL is one the proxy cannot reach (localhost, private, file://),
 *      because the modal is where the extension hint lives;
 *   3. the create failed: the modal opens pre-filled with the URL that failed
 *      and carries the server's reason inline, so the user edits and retries
 *      instead of reading a toast that has already faded.
 *
 * openModal() is the deliberate escape hatch ("Capture a different page" in
 * the split button and the Projects menus): a different page or site than
 * the one the project resolves to. Once inside a live review the header's
 * URL bar already moves between pages of the SAME site (review-guest-app
 * sections/0070), so this covers the other-site case and the user who wants
 * to type the page before the review opens.
 *
 * Streamed (Steel) sessions degrade to the normal review page, as on the
 * Schedule page: the full-screen InteractiveReview needs a host component
 * and ENABLE_STREAMED_TEAM_REVIEW is off in production.
 *
 * Every entry emits `capture_issues_clicked` (surface, path, url_source),
 * because the modal path was unmeasurable before: the dashboard runs no
 * autocapture (measured 2026-09-10: 0 `$autocapture` rows on app.overlayqa.com
 * in 30 days) and the modal emitted nothing. Human gesture, one event per
 * click; the in-flight guard means a double click emits once.
 */
import { useCallback, useRef, useState } from 'react';
import { useCreateLiveReview } from './useClientReviews';
import { buildCreatedReviewUrl } from '@/lib/createdReviewUrl';
import { openCreatedReview } from '@/lib/openCreatedReview';
import { capture } from '@/lib/analytics';
import { log } from '@/lib/logger';
import type { CaptureIssuesUrl } from '@/lib/captureIssuesUrl';
import { LiveReviewModal } from '@/components/projects/LiveReviewModal';

export type CaptureIssuesSurface = 'issues_toolbar' | 'issues_empty_state' | 'projects_menu';

export interface StartCaptureInput {
  projectId: string;
  resolved: CaptureIssuesUrl;
  surface: CaptureIssuesSurface;
}

export interface OpenCaptureModalInput {
  projectId: string;
  /** Pre-fill; the user came here to change it, so it is a starting point, never a decision. */
  initialUrl?: string;
  surface: CaptureIssuesSurface;
}

interface ModalState {
  projectId: string;
  initialUrl?: string;
  initialError?: string;
}

const NO_LINK_ERROR = 'The review was created but did not come back with a link. Try again.';

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return 'Failed to create review';
}

export function useCaptureIssues({ extensionDetected, canCaptureProject }: {
  extensionDetected: boolean;
  /** Rechecked on render so a role refresh also closes an already-open form. */
  canCaptureProject?: (projectId: string) => boolean;
}) {
  const createLiveReview = useCreateLiveReview();
  const [pendingProjectId, setPendingProjectId] = useState<string | null>(null);
  // The ref is the gate (a click lands before the state re-renders); the
  // state is what the button reads to show "Opening...".
  const pendingRef = useRef<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);

  const openModal = useCallback((input: OpenCaptureModalInput) => {
    if (canCaptureProject && !canCaptureProject(input.projectId)) return;
    log.info('[useCaptureIssues] openModal', { projectId: input.projectId, surface: input.surface, hasInitialUrl: !!input.initialUrl });
    capture('capture_issues_clicked', { surface: input.surface, path: 'modal', url_source: 'other_page', project_id: input.projectId });
    setModal({ projectId: input.projectId, initialUrl: input.initialUrl });
  }, [canCaptureProject]);

  const start = useCallback(
    (input: StartCaptureInput) => {
      const { projectId, resolved, surface } = input;
      if (canCaptureProject && !canCaptureProject(projectId)) return;
      if (!resolved.url || !resolved.reachable) {
        log.info('[useCaptureIssues] no reachable URL, asking', { projectId, surface, source: resolved.source, reason: resolved.unreachableReason });
        capture('capture_issues_clicked', {
          surface,
          path: 'modal',
          url_source: resolved.source,
          unreachable_reason: resolved.unreachableReason ?? undefined,
          project_id: projectId,
        });
        setModal({ projectId, initialUrl: resolved.url ?? undefined });
        return;
      }
      if (pendingRef.current) return;
      pendingRef.current = projectId;
      setPendingProjectId(projectId);
      log.info('[useCaptureIssues] creating review directly', { projectId, surface, source: resolved.source, url: resolved.url });
      capture('capture_issues_clicked', { surface, path: 'direct', url_source: resolved.source, project_id: projectId });
      const pageUrl = resolved.url;
      createLiveReview
        .mutateAsync({ projectId, pageUrl })
        .then((data) => {
          const fullUrl = buildCreatedReviewUrl({
            shareUrl: data?.shareUrl,
            liveAvailable: data?.liveAvailable,
            extensionDetected,
            authHandoff: data?.authHandoff,
          });
          if (!fullUrl) {
            // A 201 with no shareUrl is a server-side surprise, not a user
            // error. Say so where the user can act, never navigate to "null".
            log.error('[useCaptureIssues] create returned no shareUrl', { projectId });
            setModal({ projectId, initialUrl: pageUrl, initialError: NO_LINK_ERROR });
            return;
          }
          log.info('[useCaptureIssues] review created', { projectId, modeSwitched: data?.modeSwitched, liveAvailable: data?.liveAvailable });
          openCreatedReview(fullUrl, false);
        })
        .catch((err: unknown) => {
          log.warn('[useCaptureIssues] create failed, falling back to the modal', { projectId, error: errorMessage(err) });
          setModal({ projectId, initialUrl: pageUrl, initialError: errorMessage(err) });
        })
        .finally(() => {
          // finally, not then: a FAILED create must release the guard too.
          pendingRef.current = null;
          setPendingProjectId(null);
        });
    },
    [createLiveReview, extensionDetected, canCaptureProject],
  );

  const modalElement = modal && (!canCaptureProject || canCaptureProject(modal.projectId)) ? (
    <LiveReviewModal
      projectId={modal.projectId}
      initialUrl={modal.initialUrl}
      initialError={modal.initialError}
      onClose={() => setModal(null)}
    />
  ) : null;

  return { start, openModal, pendingProjectId, modal: modalElement };
}
